import { defineStore } from 'pinia'

import * as salesKpiMonthApi from '../services/api/sales-kpi-month'

export interface IKpiMonth {
  name: string
  value?: string
  ratio: number
  self_grading_mdht: number
  self_grading_point: number
  graded_mdht: number
  graded_point: number
  note?: string
}

export interface ISalesKpiMonthData {
  id: number
  status: string
  note: string
  reason: string
  month: string
  quarter: string
  sales_target: number
  sales_result: number
  percent_reward: number
  kpi: IKpiMonth[]
  assignee_id: number
  assignee: any
}

export const useSalesKpiMonthStore = defineStore('salesKpiMonth', {
  state: () => ({
    salesKpiMonth: {
      id: 0,
      status: '',
      note: '',
      reason: '',
      month: '',
      quarter: '',
      sales_target: 0,
      sales_result: 0,
      percent_reward: 0,
      kpi: [] as IKpiMonth[],
      assignee_id: 0,
      assignee: null,
    } as ISalesKpiMonthData,
    isLoading: false,
  }),

  actions: {
    setSalesKpiMonth(data: ISalesKpiMonthData) {
      this.salesKpiMonth = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async getSalesKpiMonth(id: number) {
      try {
        this.setLoading(true)
        const { data } = await salesKpiMonthApi.get(id)
        this.setSalesKpiMonth(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updateSalesKpiMonth(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await salesKpiMonthApi.update(this.salesKpiMonth.id, payload)
        this.setSalesKpiMonth(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async applySalesKpiMonth() {
      try {
        this.setLoading(true)
        const { data } = await salesKpiMonthApi.apply(this.salesKpiMonth.id)
        this.setSalesKpiMonth(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async rejectSalesKpiMonth(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await salesKpiMonthApi.reject(this.salesKpiMonth.id, payload)
        this.setSalesKpiMonth(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async remakeSalesKpiMonth(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await salesKpiMonthApi.remake(this.salesKpiMonth.id, payload)
        this.setSalesKpiMonth(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async approveSalesKpiMonth() {
      try {
        this.setLoading(true)
        const { data } = await salesKpiMonthApi.approve(this.salesKpiMonth.id)
        this.setSalesKpiMonth(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})

export const useSalesKpiMonthListStore = defineStore('salesKpiMonthList', {
  state: () => ({
    count: 0,
    total: 0,
    salesKpiMonths: [] as ISalesKpiMonthData[],
    isLoading: false,
  }),

  actions: {
    setCount(data: number) {
      this.count = data
    },

    setSalesKpiMonths(data: ISalesKpiMonthData[]) {
      this.salesKpiMonths = data
    },

    setTotal(value: number) {
      this.total = value
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async countSalesKpiMonths() {
      try {
        this.setLoading(true)
        const { data } = await salesKpiMonthApi.count()
        this.setCount(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listSalesKpiMonths(params: any) {
      try {
        this.setLoading(true)
        const result = await salesKpiMonthApi.list(params)
        const { data, total } = result as any
        this.setSalesKpiMonths(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllSalesKpiMonths(params: any) {
      try {
        this.setLoading(true)
        const result = await salesKpiMonthApi.listAll(params)
        const { data, total } = result as any
        this.setSalesKpiMonths(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})
